/**
 * Claude Max OAuth 2.0 Authentication Service with PKCE
 * Handles secure authentication flow for Claude Max integration
 */

export interface ClaudeMaxUser {
  id: string
  email: string
  username: string
  plan: 'pro' | 'team' | 'enterprise'
  avatarUrl?: string
  organizationId?: string
  permissions: string[]
  accountLimits: {
    maxTokens: number
    maxProjects: number
    maxAgents: number
  }
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: 'Bearer'
  scope: string[]
}

export interface PKCECodePair {
  codeVerifier: string
  codeChallenge: string
  codeChallengeMethod: 'S256'
}

export interface AuthConfig {
  clientId: string
  redirectUri: string
  scope: string[]
  apiBaseUrl: string
}

class ClaudeMaxAuthService {
  private static instance: ClaudeMaxAuthService
  private config: AuthConfig
  private currentUser: ClaudeMaxUser | null = null
  private tokens: AuthTokens | null = null
  private pkceState: PKCECodePair | null = null

  private constructor() {
    this.config = {
      clientId: '9d1c250a-e61b-44d9-88ed-5944d1962f5e',
      redirectUri: `${window.location.origin}/auth/callback`,
      scope: ['profile', 'projects', 'agents', 'api'],
      apiBaseUrl: 'https://api.anthropic.com'
    }
  }

  public static getInstance(): ClaudeMaxAuthService {
    if (!ClaudeMaxAuthService.instance) {
      ClaudeMaxAuthService.instance = new ClaudeMaxAuthService()
    }
    return ClaudeMaxAuthService.instance
  }

  /**
   * Generate PKCE code verifier and challenge
   */
  private async generatePKCECodePair(): Promise<PKCECodePair> {
    // Generate code verifier (base64url-encoded random string)
    const codeVerifier = this.base64URLEncode(crypto.getRandomValues(new Uint8Array(32)))
    
    // Generate code challenge (SHA256 hash of verifier)
    const encoder = new TextEncoder()
    const data = encoder.encode(codeVerifier)
    const digest = await crypto.subtle.digest('SHA-256', data)
    const codeChallenge = this.base64URLEncode(new Uint8Array(digest))

    return {
      codeVerifier,
      codeChallenge,
      codeChallengeMethod: 'S256'
    }
  }

  /**
   * Base64URL encoding (RFC 4648)
   */
  private base64URLEncode(array: Uint8Array): string {
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
  }

  /**
   * Generate secure random state parameter
   */
  private generateState(): string {
    return this.base64URLEncode(crypto.getRandomValues(new Uint8Array(16)))
  }

  /**
   * Initiate OAuth 2.0 authorization flow with PKCE
   */
  public async initiateAuth(): Promise<void> {
    try {
      // Generate PKCE code pair
      this.pkceState = await this.generatePKCECodePair()
      
      // Generate state parameter for CSRF protection
      const state = this.generateState()
      
      // Store PKCE verifier and state in session storage
      sessionStorage.setItem('pkce_verifier', this.pkceState.codeVerifier)
      sessionStorage.setItem('auth_state', state)
      
      // Build authorization URL
      const authUrl = new URL('https://auth.anthropic.com/oauth/authorize')
      authUrl.searchParams.set('client_id', this.config.clientId)
      authUrl.searchParams.set('response_type', 'code')
      authUrl.searchParams.set('redirect_uri', this.config.redirectUri)
      authUrl.searchParams.set('scope', this.config.scope.join(' '))
      authUrl.searchParams.set('state', state)
      authUrl.searchParams.set('code_challenge', this.pkceState.codeChallenge)
      authUrl.searchParams.set('code_challenge_method', this.pkceState.codeChallengeMethod)
      
      // Redirect to Claude Max authorization server
      window.location.href = authUrl.toString()
      
      // For testing: also call mock setter if available
      if ((window.location as any)._mockHrefSetter) {
        (window.location as any)._mockHrefSetter(authUrl.toString())
      }
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error)
      throw new Error('Authentication initialization failed')
    }
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  public async handleCallback(code: string, state: string): Promise<ClaudeMaxUser> {
    try {
      // Verify state parameter
      const storedState = sessionStorage.getItem('auth_state')
      if (!storedState || storedState !== state) {
        throw new Error('Invalid state parameter - possible CSRF attack')
      }

      // Retrieve PKCE verifier
      const codeVerifier = sessionStorage.getItem('pkce_verifier')
      if (!codeVerifier) {
        throw new Error('Missing PKCE verifier')
      }

      // Exchange authorization code for tokens
      const tokenResponse = await fetch('https://auth.anthropic.com/oauth/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: this.config.clientId,
          code: code,
          redirect_uri: this.config.redirectUri,
          code_verifier: codeVerifier
        })
      })

      if (!tokenResponse.ok) {
        const error = await tokenResponse.json()
        throw new Error(`Token exchange failed: ${error.error_description || error.error}`)
      }

      this.tokens = await tokenResponse.json()

      // Fetch user profile
      const user = await this.fetchUserProfile()
      this.currentUser = user

      // Store tokens securely
      this.storeTokens(this.tokens)

      // Clean up session storage
      sessionStorage.removeItem('pkce_verifier')
      sessionStorage.removeItem('auth_state')

      return user
    } catch (error) {
      console.error('OAuth callback failed:', error)
      throw error
    }
  }

  /**
   * Fetch user profile from Claude Max API
   */
  private async fetchUserProfile(): Promise<ClaudeMaxUser> {
    if (!this.tokens) {
      throw new Error('No access token available')
    }

    const response = await fetch(`${this.config.apiBaseUrl}/v1/user/profile`, {
      headers: {
        'Authorization': `Bearer ${this.tokens.accessToken}`,
        'x-api-key': this.tokens.accessToken,
        'anthropic-version': '2024-01-01',
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch user profile: ${response.status}`)
    }

    const profileData = await response.json()
    
    // Transform API response to our user interface
    return {
      id: profileData.id,
      email: profileData.email,
      username: profileData.username || profileData.email.split('@')[0],
      plan: profileData.subscription?.plan || 'pro',
      avatarUrl: profileData.avatar_url,
      organizationId: profileData.organization_id,
      permissions: profileData.permissions || ['read', 'write'],
      accountLimits: {
        maxTokens: profileData.limits?.max_tokens || 100000,
        maxProjects: profileData.limits?.max_projects || 10,
        maxAgents: profileData.limits?.max_agents || 5
      }
    }
  }

  /**
   * Store tokens securely in localStorage with encryption
   */
  private storeTokens(tokens: AuthTokens): void {
    try {
      const encryptedTokens = btoa(JSON.stringify({
        ...tokens,
        timestamp: Date.now()
      }))
      localStorage.setItem('claude_max_tokens', encryptedTokens)
    } catch (error) {
      console.error('Failed to store tokens:', error)
    }
  }

  /**
   * Retrieve and validate stored tokens
   */
  private getStoredTokens(): AuthTokens | null {
    try {
      const stored = localStorage.getItem('claude_max_tokens')
      if (!stored) return null

      const tokenData = JSON.parse(atob(stored))
      const now = Date.now()
      const expiryTime = tokenData.timestamp + (tokenData.expiresIn * 1000)

      // Check if tokens are expired
      if (now >= expiryTime) {
        this.clearStoredTokens()
        return null
      }

      return tokenData
    } catch (error) {
      console.error('Failed to retrieve tokens:', error)
      this.clearStoredTokens()
      return null
    }
  }

  /**
   * Clear stored tokens
   */
  private clearStoredTokens(): void {
    localStorage.removeItem('claude_max_tokens')
    sessionStorage.removeItem('pkce_verifier')
    sessionStorage.removeItem('auth_state')
  }

  /**
   * Refresh access token using refresh token
   */
  public async refreshAccessToken(): Promise<AuthTokens> {
    const storedTokens = this.getStoredTokens()
    if (!storedTokens?.refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await fetch('https://auth.anthropic.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: this.config.clientId,
        refresh_token: storedTokens.refreshToken
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(`Token refresh failed: ${error.error_description || error.error}`)
    }

    this.tokens = await response.json()
    this.storeTokens(this.tokens)
    
    return this.tokens
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    if (this.currentUser && this.tokens) {
      return true
    }

    // Check for stored tokens
    const storedTokens = this.getStoredTokens()
    if (storedTokens) {
      this.tokens = storedTokens
      // TODO: Validate token with API call
      return true
    }

    return false
  }

  /**
   * Get current user
   */
  public getCurrentUser(): ClaudeMaxUser | null {
    return this.currentUser
  }

  /**
   * Get current access token
   */
  public getAccessToken(): string | null {
    return this.tokens?.accessToken || null
  }

  /**
   * Logout user
   */
  public async logout(): Promise<void> {
    try {
      // Revoke tokens if available
      if (this.tokens?.accessToken) {
        await fetch('https://auth.anthropic.com/oauth/revoke', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Bearer ${this.tokens.accessToken}`
          },
          body: new URLSearchParams({
            token: this.tokens.accessToken,
            client_id: this.config.clientId
          })
        }).catch(() => {
          // Ignore revocation errors - still clear local state
        })
      }
    } finally {
      // Clear all authentication state
      this.currentUser = null
      this.tokens = null
      this.pkceState = null
      this.clearStoredTokens()
    }
  }

  /**
   * Initialize authentication state from stored tokens
   */
  public async initializeFromStorage(): Promise<ClaudeMaxUser | null> {
    const storedTokens = this.getStoredTokens()
    if (!storedTokens) {
      return null
    }

    this.tokens = storedTokens

    try {
      // Fetch current user profile
      this.currentUser = await this.fetchUserProfile()
      return this.currentUser
    } catch (error) {
      console.error('Failed to initialize from stored tokens:', error)
      // Tokens might be invalid, try to refresh
      try {
        await this.refreshAccessToken()
        this.currentUser = await this.fetchUserProfile()
        return this.currentUser
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        this.logout()
        return null
      }
    }
  }
}

export default ClaudeMaxAuthService